/**
 * ExtractionReviewPanel.jsx
 *
 * Displays AI-extracted manuscript metadata in editable sections.
 * All fields are pre-filled by the extraction pipeline and can be adjusted
 * before applying. Each section can be applied independently or all at once.
 *
 * Features:
 *   - Extraction Draft Versioning: browse & roll back to last 3 runs
 *   - Chapter-Level Re-Extract: re-run on a single chapter
 *   - Codex Auto-Link: shows chapter appearances on each codex entry
 *
 * Props:
 *   projectId   string  — project to show/edit
 *   onApplied   fn      — called after a successful apply
 *   compact     bool    — when true, renders without Dialog wrapper (embed mode)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCircle2, Loader2, RefreshCw, Save, ChevronDown, ChevronUp,
  BookOpen, Users, Globe, ScrollText, Mic, BarChart2, AlertCircle,
  Sparkles, Pencil, History, BookMarked, MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { extractionApi } from '@/lib/api';

// ── Status helpers ────────────────────────────────────────────────────────────
const STATUS_LABELS = {
  not_started:   'Not started',
  pending:       'Queued…',
  running_pass1: 'Analysing story structure…',
  running_pass2: 'Extracting characters…',
  running_pass3: 'Mapping world & codex…',
  done:          'Extraction complete',
  failed:        'Extraction failed',
};

const PASS_PROGRESS = {
  pending:       5,
  running_pass1: 30,
  running_pass2: 65,
  running_pass3: 90,
  done:          100,
  failed:        0,
};

function StatusBar({ status, error }) {
  const label   = STATUS_LABELS[status] || status;
  const pct     = PASS_PROGRESS[status] ?? 0;
  const running = status?.startsWith('running') || status === 'pending';
  const failed  = status === 'failed';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-[12px]">
        <span className={`flex items-center gap-1.5 font-medium ${failed ? 'text-destructive' : 'text-muted-foreground'}`}>
          {running && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {status === 'done' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
          {failed && <AlertCircle className="h-3.5 w-3.5" />}
          {label}
        </span>
        {running && <span className="text-muted-foreground">{pct}%</span>}
      </div>
      {running && (
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {failed && error && (
        <p className="text-[11px] text-destructive">{error}</p>
      )}
    </div>
  );
}

// ── Generic collapsible section ───────────────────────────────────────────────
function Section({ icon: Icon, title, badge, children, defaultOpen = true, onApply, applying }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-md border border-border overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 bg-muted/40 px-3 py-2 text-left hover:bg-muted/60 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-2 text-[12.5px] font-semibold">
          {Icon && <Icon className="h-3.5 w-3.5 text-primary" />}
          {title}
          {badge != null && (
            <Badge variant="outline" className="text-[9.5px] h-4">{badge}</Badge>
          )}
        </span>
        <span className="flex items-center gap-2">
          {onApply && (
            <Button
              size="sm" variant="outline"
              className="h-6 px-2 text-[10.5px] gap-1"
              onClick={(e) => { e.stopPropagation(); onApply(); }}
              disabled={applying}
              data-testid={`apply-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Apply
            </Button>
          )}
          {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </span>
      </button>
      {open && <div className="px-3 py-3 space-y-2 text-[12px]">{children}</div>}
    </div>
  );
}

// ── Field editors ─────────────────────────────────────────────────────────────
function FieldRow({ label, value, onChange, multiline = false, rows = 3 }) {
  return (
    <div className="space-y-0.5">
      <label className="text-[10.5px] font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      {multiline ? (
        <textarea
          className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px] outline-none focus:border-primary resize-y min-h-[60px]"
          rows={rows}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type="text"
          className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px] outline-none focus:border-primary"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

// ── Beat / subplot list editor ────────────────────────────────────────────────
function BeatList({ items = [], onChange, namePlaceholder = 'Beat name', descPlaceholder = 'Description' }) {
  const update = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { name: '', description: '' }]);

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-start rounded-sm border border-border/60 p-2 bg-muted/20">
          <div className="flex-1 space-y-1">
            <input
              className="w-full rounded-sm border border-border bg-background px-1.5 py-1 text-[11.5px] outline-none focus:border-primary font-medium"
              placeholder={namePlaceholder}
              value={item.name || item.title || ''}
              onChange={(e) => update(i, 'name', e.target.value)}
            />
            <textarea
              className="w-full rounded-sm border border-border bg-background px-1.5 py-1 text-[11.5px] outline-none focus:border-primary resize-none"
              rows={2}
              placeholder={descPlaceholder}
              value={item.description || ''}
              onChange={(e) => update(i, 'description', e.target.value)}
            />
          </div>
          <button
            type="button"
            className="mt-1 text-muted-foreground hover:text-destructive"
            onClick={() => remove(i)}
          >×</button>
        </div>
      ))}
      <button
        type="button"
        className="text-[11px] text-primary hover:underline"
        onClick={add}
      >+ Add</button>
    </div>
  );
}

// ── Character editor ──────────────────────────────────────────────────────────
function CharacterCard({ char, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const update = (path, val) => {
    if (path.includes('.')) {
      const [tier, key] = path.split('.');
      onChange({ ...char, [tier]: { ...(char[tier] || {}), [key]: val } });
    } else {
      onChange({ ...char, [path]: val });
    }
  };

  const TIERS = [
    { key: 'identity',      label: 'Identity',        fields: ['full_name','aliases','gender','age','occupation','birthplace','current_location'] },
    { key: 'physical',      label: 'Physical',         fields: ['build','hair','eyes','skin','distinguishing_marks','style','voice_and_gait'] },
    { key: 'personality',   label: 'Personality',      fields: ['strengths','weaknesses','quirks','values','fears','desires','need'] },
    { key: 'backstory',     label: 'Backstory',        fields: ['origin','family','formative_event','key_relationships','secrets'] },
    { key: 'integration',   label: 'Story Role',       fields: ['story_role','arc_summary','key_scenes','thematic_resonance','pov_notes'] },
    { key: 'voice_regional','label': 'Voice & Dialect', fields: ['register','dialect_notes','emotional_register','taboos'] },
  ];

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-3 py-2 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-2">
          <span className="font-medium text-[12.5px]">{char.name || 'Unnamed'}</span>
          {char.role && <Badge variant="outline" className="text-[9px] h-4">{char.role}</Badge>}
        </span>
        <span className="flex items-center gap-2">
          <button
            type="button"
            className="text-muted-foreground hover:text-destructive text-[11px]"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >Remove</button>
          {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </span>
      </button>
      {open && (
        <div className="px-3 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="Name" value={char.name} onChange={(v) => update('name', v)} />
            <FieldRow label="Role" value={char.role} onChange={(v) => update('role', v)} />
          </div>
          {TIERS.map(({ key, label, fields }) => (
            <details key={key} className="rounded-sm border border-border/60">
              <summary className="cursor-pointer px-2 py-1.5 text-[11.5px] font-medium text-muted-foreground hover:text-foreground list-none flex items-center gap-1">
                <ChevronDown className="h-3 w-3" /> {label}
              </summary>
              <div className="px-2 py-2 grid grid-cols-2 gap-2">
                {fields.map((f) => (
                  <FieldRow
                    key={f}
                    label={f.replace(/_/g, ' ')}
                    value={(char[key] || {})[f]}
                    onChange={(v) => update(`${key}.${f}`, v)}
                    multiline={['strengths','weaknesses','quirks','values','fears','formative_event','origin','secrets','dialect_notes','arc_summary','thematic_resonance','pov_notes','key_scenes','key_relationships'].includes(f)}
                    rows={2}
                  />
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Codex entry editor ────────────────────────────────────────────────────────
function CodexCard({ entry, onChange, onRemove }) {
  return (
    <div className="flex gap-2 items-start rounded-sm border border-border/60 p-2 bg-muted/20">
      <div className="flex-1 space-y-1.5">
        <div className="grid grid-cols-2 gap-2">
          <input
            className="rounded-sm border border-border bg-background px-1.5 py-1 text-[11.5px] font-medium outline-none focus:border-primary"
            placeholder="Name"
            value={entry.name || ''}
            onChange={(e) => onChange({ ...entry, name: e.target.value })}
          />
          <select
            className="rounded-sm border border-border bg-background px-1.5 py-1 text-[11.5px] outline-none focus:border-primary"
            value={entry.entry_type || 'lore'}
            onChange={(e) => onChange({ ...entry, entry_type: e.target.value })}
          >
            {['location','faction','object','lore','concept','event','system'].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <textarea
          className="w-full rounded-sm border border-border bg-background px-1.5 py-1 text-[11.5px] outline-none focus:border-primary resize-none"
          rows={2}
          placeholder="Summary"
          value={entry.summary || ''}
          onChange={(e) => onChange({ ...entry, summary: e.target.value })}
        />
        <input
          className="w-full rounded-sm border border-border bg-background px-1.5 py-1 text-[10.5px] outline-none focus:border-primary text-muted-foreground"
          placeholder="tags, comma-separated"
          value={(entry.tags || []).join(', ')}
          onChange={(e) => onChange({ ...entry, tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })}
        />
        {/* Chapter appearances (auto-linked on apply) */}
        {entry.chapter_appearances && entry.chapter_appearances.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap mt-0.5">
            <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-[10px] text-muted-foreground">Appears in:</span>
            {entry.chapter_appearances.map((ca) => (
              <span
                key={ca.chapter_id}
                className="inline-flex items-center rounded px-1 py-0 text-[9.5px] font-medium bg-primary/10 text-primary"
              >
                Ch {ca.chapter_number}
              </span>
            ))}
          </div>
        )}
      </div>
      <button type="button" className="text-muted-foreground hover:text-destructive mt-1" onClick={onRemove}>×</button>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function ExtractionReviewPanel({ projectId, onApplied, compact = false }) {
  const [draft,        setDraft]        = useState(null);
  const [applying,     setApplying]     = useState(null); // section name or 'all'
  const [dirty,        setDirty]        = useState(false);
  // Versioning
  const [versions,     setVersions]     = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [activating,   setActivating]   = useState(false);
  // Chapter re-extract
  const [chapters,     setChapters]     = useState([]);
  const [chExtract,    setChExtract]    = useState(null); // chapter_id being extracted
  const [showChPicker, setShowChPicker] = useState(false);
  const saveTimerRef  = useRef(null);
  const pollTimerRef  = useRef(null);

  // ── Polling ─────────────────────────────────────────────────────────────
  const fetchDraft = useCallback(async () => {
    if (!projectId) return;
    try {
      const d = await extractionApi.getDraft(projectId);
      setDraft(d);
      return d;
    } catch {
      return null;
    }
  }, [projectId]);

  const fetchVersions = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await extractionApi.listVersions(projectId);
      setVersions(res.versions || []);
    } catch { /* non-fatal */ }
  }, [projectId]);

  const fetchChapters = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/chapters`,
        { credentials: 'include' }
      );
      const data = await res.json();
      setChapters((data.chapters || data || []).sort((a, b) => (a.number || 0) - (b.number || 0)));
    } catch { /* non-fatal */ }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    fetchDraft().then((d) => {
      if (!d) return;
      const running = d.status?.startsWith('running') || d.status === 'pending';
      if (running) startPolling();
    });
    fetchVersions();
    fetchChapters();
    return () => {
      clearInterval(pollTimerRef.current);
      clearTimeout(saveTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const startPolling = () => {
    clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      const d = await fetchDraft();
      if (!d) return;
      if (!d.status?.startsWith('running') && d.status !== 'pending') {
        clearInterval(pollTimerRef.current);
      }
    }, 2500);
  };

  // ── Draft field change (auto-save debounced) ─────────────────────────────
  const updateField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await extractionApi.updateDraft(projectId, { [field]: value });
        setDirty(false);
      } catch { /* silent */ }
    }, 800);
  };

  // ── Version rollback ─────────────────────────────────────────────────────
  const handleActivateVersion = async (draftId) => {
    setActivating(true);
    try {
      await extractionApi.activateVersion(projectId, draftId);
      await fetchDraft();
      await fetchVersions();
      setShowVersions(false);
      toast.success('Rolled back to selected version.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rollback failed');
    } finally {
      setActivating(false);
    }
  };

  // ── Chapter re-extract ───────────────────────────────────────────────────
  const handleChapterExtract = async (chapterId) => {
    setChExtract(chapterId);
    setShowChPicker(false);
    try {
      await extractionApi.extractChapter(projectId, chapterId);
      toast.info('Chapter re-extract queued — polling for updates…');
      // Poll until chapter_extract_status settles
      const poll = setInterval(async () => {
        const d = await fetchDraft();
        const status = d?.chapter_extract_status || '';
        if (status && !status.endsWith('…') && !status.toLowerCase().includes('extract')) {
          clearInterval(poll);
          setChExtract(null);
          toast.success(`Chapter extraction complete: ${status}`);
        }
      }, 2500);
      setTimeout(() => { clearInterval(poll); setChExtract(null); }, 120_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Chapter extraction failed');
      setChExtract(null);
    }
  };

  // ── Apply ────────────────────────────────────────────────────────────────
  const applySection = async (sections) => {
    const key = Array.isArray(sections) ? sections.join('+') : sections;
    setApplying(key);
    try {
      // Flush any dirty changes first
      if (dirty) {
        await extractionApi.updateDraft(projectId, {
          synopsis: draft.synopsis, genre: draft.genre,
          logline: draft.logline, premise: draft.premise,
          themes: draft.themes, tone: draft.tone,
          pov: draft.pov, tense: draft.tense,
          structure_type: draft.structure_type,
          voice_notes: draft.voice_notes, pacing_notes: draft.pacing_notes,
          plot_beats: draft.plot_beats, subplots: draft.subplots,
          characters: draft.characters, codex_entries: draft.codex_entries,
        });
        setDirty(false);
      }
      await extractionApi.apply(projectId, Array.isArray(sections) ? sections : [sections]);
      toast.success(`Applied ${key} to project`);
      onApplied?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(null);
    }
  };

  // ── Trigger extraction ───────────────────────────────────────────────────
  const triggerExtraction = async () => {
    try {
      await extractionApi.trigger(projectId);
      setDraft((prev) => ({ ...prev, status: 'pending' }));
      startPolling();
      toast.info('AI extraction started…');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start extraction');
    }
  };

  if (!draft) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="text-[12px] text-muted-foreground">Loading extraction status…</span>
      </div>
    );
  }

  const isRunning = draft.status?.startsWith('running') || draft.status === 'pending';
  const isDone    = draft.status === 'done';
  const isFailed  = draft.status === 'failed';
  const notStarted= draft.status === 'not_started';

  return (
    <div className="space-y-4">
      {/* Status + controls */}
      <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
        <StatusBar status={draft.status} error={draft.error} />
        <div className="flex items-center gap-2 flex-wrap">
          {(notStarted || isFailed) && (
            <Button size="sm" className="gap-1.5" onClick={triggerExtraction} data-testid="extraction-trigger-btn">
              <Sparkles className="h-3.5 w-3.5" />
              {isFailed ? 'Retry extraction' : 'Run AI extraction'}
            </Button>
          )}
          {isRunning && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={fetchDraft}>
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
          )}
          {isDone && (
            <>
              <Button
                size="sm" className="gap-1.5"
                onClick={() => applySection(['framework','characters','codex','plot'])}
                disabled={!!applying}
                data-testid="apply-all-btn"
              >
                {applying === 'framework+characters+codex+plot'
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Applying…</>
                  : <><CheckCircle2 className="h-3.5 w-3.5" />Apply all to project</>}
              </Button>
              <Button size="sm" variant="ghost" className="gap-1.5" onClick={triggerExtraction} data-testid="extraction-rerun-btn">
                <RefreshCw className="h-3.5 w-3.5" /> Re-run
              </Button>
              {dirty && <span className="text-[10.5px] text-amber-600 flex items-center gap-1"><Pencil className="h-3 w-3" />Unsaved edits</span>}
            </>
          )}
        </div>

        {/* Version picker */}
        {versions.length > 1 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowVersions((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
              data-testid="versions-toggle-btn"
            >
              <History className="h-3.5 w-3.5" />
              {versions.length} saved version{versions.length > 1 ? 's' : ''}
              {showVersions ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {showVersions && (
              <div className="absolute z-10 top-full left-0 mt-1 w-72 rounded-md border border-border bg-background shadow-lg p-2 space-y-1">
                {versions.map((v) => (
                  <div key={v.draft_id} className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5 hover:bg-muted/40">
                    <div>
                      <span className="text-[11px] font-medium">v{v.version}</span>
                      <span className="ml-2 text-[10px] text-muted-foreground">{new Date(v.created_at).toLocaleString()}</span>
                      {v.is_latest && <Badge variant="outline" className="ml-2 text-[9px] h-3.5">current</Badge>}
                    </div>
                    {!v.is_latest && (
                      <Button
                        size="sm" variant="outline"
                        className="h-6 px-2 text-[10px]"
                        disabled={activating}
                        onClick={() => handleActivateVersion(v.draft_id)}
                        data-testid={`activate-version-${v.draft_id}`}
                      >
                        {activating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Restore'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chapter re-extract */}
        {isDone && chapters.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowChPicker((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
              data-testid="chapter-reextract-toggle"
            >
              <BookMarked className="h-3.5 w-3.5" />
              Re-extract a single chapter
              {showChPicker ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {showChPicker && (
              <div className="absolute z-10 top-full left-0 mt-1 w-72 rounded-md border border-border bg-background shadow-lg p-2 space-y-1 max-h-48 overflow-y-auto">
                {chapters.map((ch) => (
                  <button
                    key={ch.id}
                    type="button"
                    className="w-full text-left flex items-center gap-2 rounded-sm px-2 py-1.5 text-[11.5px] hover:bg-muted/50 disabled:opacity-50"
                    disabled={chExtract === ch.id}
                    onClick={() => handleChapterExtract(ch.id)}
                    data-testid={`ch-reextract-${ch.id}`}
                  >
                    {chExtract === ch.id
                      ? <Loader2 className="h-3 w-3 animate-spin text-primary" />
                      : <Sparkles className="h-3 w-3 text-muted-foreground" />}
                    Ch {ch.number}: {ch.title || `Chapter ${ch.number}`}
                  </button>
                ))}
              </div>
            )}
            {draft.chapter_extract_status && (
              <p className="mt-1 text-[10.5px] text-primary">{draft.chapter_extract_status}</p>
            )}
          </div>
        )}
      </div>

      {/* Sections — shown once extraction has at least started */}
      {(isDone || isRunning) && (
        <div className="space-y-3">

          {/* ── Framework ────────────────────────────────────────────────── */}
          <Section
            icon={ScrollText} title="Novel Framework"
            onApply={isDone ? () => applySection('framework') : undefined}
            applying={applying === 'framework'}
          >
            <FieldRow label="Genre" value={draft.genre} onChange={(v) => updateField('genre', v)} />
            <FieldRow label="Logline" value={draft.logline} onChange={(v) => updateField('logline', v)} />
            <FieldRow label="Premise" value={draft.premise} onChange={(v) => updateField('premise', v)} multiline rows={2} />
            <FieldRow label="Synopsis" value={draft.synopsis} onChange={(v) => updateField('synopsis', v)} multiline rows={5} />
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Themes" value={draft.themes} onChange={(v) => updateField('themes', v)} />
              <FieldRow label="Tone" value={draft.tone} onChange={(v) => updateField('tone', v)} />
              <FieldRow label="POV" value={draft.pov} onChange={(v) => updateField('pov', v)} />
              <FieldRow label="Tense" value={draft.tense} onChange={(v) => updateField('tense', v)} />
              <FieldRow label="Structure type" value={draft.structure_type} onChange={(v) => updateField('structure_type', v)} />
            </div>
            <FieldRow label="Voice notes" value={draft.voice_notes} onChange={(v) => updateField('voice_notes', v)} multiline rows={2} />
            <FieldRow label="Pacing notes" value={draft.pacing_notes} onChange={(v) => updateField('pacing_notes', v)} multiline rows={2} />
          </Section>

          {/* ── Plot beats ───────────────────────────────────────────────── */}
          <Section
            icon={BarChart2} title="Plot Beats"
            badge={draft.plot_beats?.length ?? 0}
            onApply={isDone ? () => applySection('framework') : undefined}
            applying={applying === 'framework'}
          >
            <BeatList
              items={draft.plot_beats || []}
              onChange={(v) => updateField('plot_beats', v)}
              namePlaceholder="Beat name (e.g. Inciting Incident)"
              descPlaceholder="What happens at this beat in this story"
            />
          </Section>

          {/* ── Subplots ─────────────────────────────────────────────────── */}
          <Section
            icon={BookOpen} title="Subplots"
            badge={draft.subplots?.length ?? 0}
            onApply={isDone ? () => applySection('framework') : undefined}
            applying={applying === 'framework'}
          >
            <BeatList
              items={draft.subplots || []}
              onChange={(v) => updateField('subplots', v)}
              namePlaceholder="Subplot title"
              descPlaceholder="Brief description of this subplot"
            />
          </Section>

          {/* ── Characters ───────────────────────────────────────────────── */}
          <Section
            icon={Users} title="Characters"
            badge={draft.characters?.length ?? 0}
            onApply={isDone ? () => applySection('characters') : undefined}
            applying={applying === 'characters'}
          >
            {isRunning && draft.status !== 'running_pass2' && draft.status !== 'running_pass3' && draft.status !== 'done' && (
              <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground py-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Extracting characters…
              </div>
            )}
            {(draft.characters || []).length === 0 && !isRunning && (
              <p className="text-[11.5px] text-muted-foreground italic">No characters extracted yet.</p>
            )}
            <div className="space-y-2">
              {(draft.characters || []).map((char, i) => (
                <CharacterCard
                  key={i}
                  char={char}
                  onChange={(updated) => {
                    const next = [...(draft.characters || [])];
                    next[i] = updated;
                    updateField('characters', next);
                  }}
                  onRemove={() => {
                    const next = (draft.characters || []).filter((_, idx) => idx !== i);
                    updateField('characters', next);
                  }}
                />
              ))}
              <button
                type="button"
                className="text-[11px] text-primary hover:underline"
                onClick={() => updateField('characters', [...(draft.characters || []), { name: '', role: 'supporting' }])}
              >+ Add character</button>
            </div>
          </Section>

          {/* ── Codex ────────────────────────────────────────────────────── */}
          <Section
            icon={Globe} title="World & Codex"
            badge={draft.codex_entries?.length ?? 0}
            onApply={isDone ? () => applySection('codex') : undefined}
            applying={applying === 'codex'}
          >
            {(draft.codex_entries || []).length === 0 && !isRunning && (
              <p className="text-[11.5px] text-muted-foreground italic">No world entries extracted yet.</p>
            )}
            <div className="space-y-2">
              {(draft.codex_entries || []).map((entry, i) => (
                <CodexCard
                  key={i}
                  entry={entry}
                  onChange={(updated) => {
                    const next = [...(draft.codex_entries || [])];
                    next[i] = updated;
                    updateField('codex_entries', next);
                  }}
                  onRemove={() => {
                    const next = (draft.codex_entries || []).filter((_, idx) => idx !== i);
                    updateField('codex_entries', next);
                  }}
                />
              ))}
              <button
                type="button"
                className="text-[11px] text-primary hover:underline"
                onClick={() => updateField('codex_entries', [...(draft.codex_entries || []), { name: '', entry_type: 'lore', summary: '', tags: [] }])}
              >+ Add entry</button>
            </div>
          </Section>

          {/* ── Voice notes ─────────────────────────────────────────────── */}
          <Section icon={Mic} title="Voice & Pacing Notes">
            <FieldRow label="Voice notes" value={draft.voice_notes} onChange={(v) => updateField('voice_notes', v)} multiline rows={3} />
            <FieldRow label="Pacing notes" value={draft.pacing_notes} onChange={(v) => updateField('pacing_notes', v)} multiline rows={3} />
          </Section>

        </div>
      )}
    </div>
  );
}
